// src/hooks/useNotificationSocket.ts
import { useEffect, useRef } from 'react';
import {
  HubConnectionBuilder,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5001';

export interface IncomingSystemNotification {
  id: number;
  category: string;
  title: string;
  body: string;
  createdAt: string;
}

export type NotificationSocketHandler = (notification: {
  id: number;
  category: string;
  title: string;
  body: string;
}) => void;

/**
 * Owns the SignalR connection to /hubs/notifications for as long as the
 * user is authenticated. Every push the server sends here is already
 * filtered to Category === "System" server-side (see NotificationService),
 * so the only client-side gate needed is the user's own msgSystemToasts
 * preference - checked live via a ref so toggling it doesn't require
 * tearing down and rebuilding the connection.
 *
 * This is purely additive to the existing ~60s poll in useNotifications:
 * onNotification only ever drives a toast, never the inbox/badge state.
 * If the connection can't establish at all (blocked WebSockets, corporate
 * proxy, etc.) the .catch() below swallows it silently - the app behaves
 * exactly as it did before this feature existed.
 */
export function useNotificationSocket(
  onNotification: NotificationSocketHandler
): void {
  const { token, isAuthenticated, user } = useAuth();

  const onNotificationRef = useRef(onNotification);
  onNotificationRef.current = onNotification;

  const msgSystemToastsRef = useRef(user?.msgSystemToasts);
  msgSystemToastsRef.current = user?.msgSystemToasts;

  useEffect(() => {
    if (!isAuthenticated || !token) return undefined;

    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${API_URL}/hubs/notifications`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      // SignalR's client defaults to LogLevel.Information, which logs the
      // full connection URL (including the access_token query param) to
      // the browser console on every connect/reconnect. Warning still
      // surfaces genuine connection problems without leaking the token.
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on(
      'ReceiveNotification',
      (payload: IncomingSystemNotification) => {
        // Explicit `=== false` so an older cached authUser (from before
        // this preference existed) defaults to "on" rather than silently
        // suppressing toasts.
        if (msgSystemToastsRef.current === false) return;
        onNotificationRef.current({
          id: payload.id,
          category: payload.category,
          title: payload.title,
          body: payload.body,
        });
      }
    );

    connection.start().catch(() => {
      // Graceful degradation - see doc comment above.
    });

    return () => {
      connection.stop().catch(() => {});
    };
  }, [isAuthenticated, token]);
}
